import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";
import {
    FlexContainer, FormField, Button, LottieLoadingIndicator, Toast, StandardModal, StandardModalActionButton,
    StandardModalActionGroup, StandardModalHelperText, StandardModalInfoCard, TextInputWithIcon,
    portalColors
} from "src/components";
import SearchIcon from "src/img/new/search-gray.svg";
import { FORM_TYPE_CREATE, GROUP_CHAT, USER_TYPE_CLIENTS, USER_TYPE_EMPLOYEES } from "src/constants/chat";
import Single from "src/scenes/Chat/components/create/Single";
import Group from "src/scenes/Chat/components/create/Group";
import LeadershipRoleToUseSelection from "src/scenes/Chat/components/create/LeadershipRoleToUseSelection";
import TypeSelection from "src/scenes/Chat/components/create/TypeSelection";
import {
    useFetchUsersList
} from "src/scenes/Chat/hooks";
import SelectUsersSection from "./SelectUsersSection";
import { MESSAGE_CLIENTS, MESSAGE_EMPLOYEES, isChatActionAllowed } from "src/constants/permissions";

export const Header = styled(FlexContainer)`
    flex-shrink: 0;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background-color: ${portalColors.surfaceSoft};
    height: 5.125rem;
    max-height: 5.125rem;
    padding: 0 2.375rem 0 1.6875rem;
    border-bottom: 1px solid ${portalColors.border};
    border-radius: 1.25rem 1.25rem 0 0;
`;

const StyledSearchInput = styled(FormField)`
    position: relative;
    border: 1px solid ${portalColors.border};
    background: ${portalColors.surface};
    color: ${portalColors.text};
    width: 100%;
    max-width: 53.9375rem;
    height: 2.5rem;
    font-size: 0.875rem;
    text-align: left;
    padding: 0 0.9375rem 0 0.9375rem;
    border-radius: 0.5rem !important;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-size: 0.875rem;
    }
`;

export const SearchInput = ({ margin, ...props }) => (
    <TextInputWithIcon
        iconSrc={SearchIcon}
        iconPosition="right"
        iconOffset="0.9375rem"
        iconWidth="24px"
        iconHeight="24px"
        maxWidth="53.9375rem"
        margin={margin}
    >
        <StyledSearchInput {...props} />
    </TextInputWithIcon>
);

export const StyledButton = styled(Button)`
    width: ${p => p.width || "8.75rem"};
    height: ${p => p.height || "2.625rem"};
    border-radius: ${({ borderRadius }) => borderRadius || "7px"} !important;
    font-size: 0.875rem;
    font-weight: 700;
    box-sizing: border-box;
    background-color: ${ p => p.backgroundColor || portalColors.text };
    color: ${ p => p.color || "#fff" };
    border: ${p => p.border || "none"};
`;

export const GridContainer = styled.div`
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18.75rem, 1fr));
    gap: 1.25rem;
    width: 100%;
    position: relative;
`;

const CreateChatModal = (props) => {
    const {
        useActiveChats: ctxUseActiveChats,
        useCreateChatModal: ctxUseCreateChatModal
    } = useContext(ChatContext);

    const {
        selectedChatListTypeOrLeadershipUuid
    } = ctxUseActiveChats;

    const {
        chatToCreate,
        closeCreateChatModal,
        handleCreateChat,
        isCurrentlyCreatingChat
    } = ctxUseCreateChatModal;

    const showDialog = chatToCreate.showModal;
    const handleClose = () => closeCreateChatModal();

    const isGroupChat = chatToCreate.chatType === GROUP_CHAT;
    const currentUser = useContext(UserContext);
    const leadershipRoles = currentUser[UserKeys.LEADERSHIP_ROLES];
    const hasLeadershipRoles = Array.isArray(leadershipRoles) && leadershipRoles.length > 0;
    const permissions = currentUser[UserKeys.PERMISSIONS];
    const { fetchUsersList } = useFetchUsersList();

    /** list of all company users */
    const [loadingFetchUsers, setLoadingFetchUsers] = useState(false);
    const [isUserListBeingSearched, setIsUserListBeingSearched] = useState(false);
    const [selectedUserListType, setSelectedUserListType] = useState("");

    const [formData, setFormData] = useState({
        allowUsersToLeave: false,
        groupChatName: "",
        leadershipRoleUuid: undefined,
        selectedUsersByType: {
            [USER_TYPE_EMPLOYEES]: [],
            [USER_TYPE_CLIENTS]: []
        }
    });

    const checkLeadershipRoleVisibility = (currentSelectedLeadershipUuid, checkerType, leadershipRoles) => {
        if (currentSelectedLeadershipUuid === undefined || currentSelectedLeadershipUuid === "") {
            return true;
        }

        const selectedLeadershipRole = leadershipRoles.find((leadershipRole) => leadershipRole.uuid === currentSelectedLeadershipUuid);
        if (selectedLeadershipRole && currentSelectedLeadershipUuid === selectedLeadershipRole.uuid) {
            if (checkerType === USER_TYPE_EMPLOYEES) {
                return selectedLeadershipRole.isVisibleToEmployees;
            } else if (checkerType === USER_TYPE_CLIENTS) {
                return selectedLeadershipRole.isVisibleToClients;
            }
        }
    };

    useEffect(() => {
        try {
            const defaultType = USER_TYPE_EMPLOYEES;
            setSelectedUserListType(defaultType);
            retrieveUpdatedUsersList(defaultType);
        } catch (err) {
            Toast.error(err);
            closeCreateChatModal();
        }
    }, []);

    const [usersByType, setUsersByType] = useState({
        [USER_TYPE_EMPLOYEES]: [],
        [USER_TYPE_CLIENTS]: []
    });

    const [searchFilteredUsersByType, setSearchFilteredUsersByType] = useState({
        [USER_TYPE_EMPLOYEES]: [],
        [USER_TYPE_CLIENTS]: []
    });

    const retrieveUpdatedUsersList = async (fetchUserType) => {
        setLoadingFetchUsers(true);
        setIsUserListBeingSearched(false);

        let users = usersByType[fetchUserType] || [];

        if (!users.length) {
            users = await fetchUsersList(fetchUserType);
        }

        if (users) {
            const validUsers = users.filter(user => !!user.matrixUserId);

            setUsersByType(prev => ({ ...prev, [fetchUserType]: validUsers }));
            setSearchFilteredUsersByType(prev => ({ ...prev, [fetchUserType]: validUsers }));
        }

        setLoadingFetchUsers(false);
    };

    const handleSelectedUsersToChat = (data, type) => {
        setFormData(prev => ({
            ...prev,
            selectedUsersByType: {
                ...prev.selectedUsersByType,
                [type]: data
            }
        }));
    };

    useEffect(() => {
        if (!hasLeadershipRoles) {
            retrieveUpdatedUsersList(selectedChatListTypeOrLeadershipUuid);
        }
    }, [showDialog]);

    const handleSetGroupChatName = (name) => setFormData({ ...formData, groupChatName: name });

    const handleSubmit = () => {
        const employeeUuids = formData.selectedUsersByType[USER_TYPE_EMPLOYEES] || [];
        const clientUuids = formData.selectedUsersByType[USER_TYPE_CLIENTS] || [];

        const allSelectedUuids = [...employeeUuids, ...clientUuids];

        const payload = {
            allowUsersToLeave: formData.allowUsersToLeave,
            employeeUuidsToChat: allSelectedUuids,
            groupChatName: formData.groupChatName || "",
            leadershipRoleUuid: formData.leadershipRoleUuid,
            isClient: clientUuids.length > 0
        };

        handleCreateChat(payload);
    };

    const handleSearch = (searchKey) => {
        const currentList = usersByType[selectedUserListType] || [];

        if (searchKey.length) {
            const filteredUsers = currentList.filter(user =>
                user.firstName.toLowerCase().includes(searchKey.toLowerCase())
            || user.lastName.toLowerCase().includes(searchKey.toLowerCase())
            );
            setIsUserListBeingSearched(true);
            setSearchFilteredUsersByType(prev => ({ ...prev, [selectedUserListType]: filteredUsers }));
        } else {
            setIsUserListBeingSearched(false);
            setSearchFilteredUsersByType(prev => ({ ...prev, [selectedUserListType]: currentList }));
        }
    };

    /**
    * @param {string} userType
    */
    const changeSelectedType = (userType) => {
        if (userType === selectedUserListType) return;
        setSelectedUserListType(userType);
        retrieveUpdatedUsersList(userType);
    };
    const dialogHeight = "min(760px, calc(100vh - 32px))";

    return (
        <StandardModal
            open={showDialog}
            onClose={handleClose}
            title={isGroupChat ? "Create Group Chat" : "New Chat"}
            widthMode="compactWide"
            paperStyle={{ maxHeight: dialogHeight }}
            headerAside={(!hasLeadershipRoles && leadershipRoles.size > 0) && (
                <TypeSelection
                    canSeeEmployeeList={isChatActionAllowed(MESSAGE_EMPLOYEES, permissions)}
                    canSeeClientList={isChatActionAllowed(MESSAGE_CLIENTS, permissions)}
                    changeSelectedType={changeSelectedType}
                    width="12.5rem"
                    border="1px solid #FFF"
                />
            )}
            footer={!isCurrentlyCreatingChat && (
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton onClick={handleClose}>Cancel</StandardModalActionButton>
                    <StandardModalActionButton $variant="primary" onClick={handleSubmit}>
                        {isGroupChat ? "Create Group" : "Message"}
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <FlexContainer style={{ height: "100%" }}>
                { isCurrentlyCreatingChat
                    ?
                    <LottieLoadingIndicator />
                    :
                    <>
                        <GridContainer>
                            <LeadershipRoleToUseSelection
                                formData={formData}
                                setFormData={setFormData}
                                checkLeadershipRoleVisibility={checkLeadershipRoleVisibility}
                                changeSelectedType={changeSelectedType}
                            />
                        </GridContainer>

                        <StandardModalInfoCard
                            background={portalColors.surfaceSoft}
                            padding="0.75rem 0.875rem"
                            style={{ margin: "0.75rem 0" }}
                        >
                            <StandardModalHelperText>
                                Can&apos;t find someone? They&apos;ll appear here once they log into the app for the first time.
                                End-to-end encryption relies on devices setting themselves up ahead of time.
                            </StandardModalHelperText>
                        </StandardModalInfoCard>

                        <SelectUsersSection
                            loadingFetchUsers={loadingFetchUsers}
                            usersList={usersByType[selectedUserListType] || []}
                            isUserListBeingSearched={isUserListBeingSearched}
                            handleClose={handleClose}
                            handleSubmit={handleSubmit}
                            currentUserType={selectedChatListTypeOrLeadershipUuid}
                            hideFooter={true}
                        >
                            { chatToCreate.chatType === GROUP_CHAT ?
                                <Group
                                    availableUsersList={[
                                        ...usersByType[USER_TYPE_EMPLOYEES],
                                        ...usersByType[USER_TYPE_CLIENTS]
                                    ]}
                                    searchFilteredUserList={
                                        searchFilteredUsersByType[selectedUserListType] || []
                                    }
                                    selectedUserListType={selectedUserListType}
                                    formType={FORM_TYPE_CREATE}
                                    handleSearch={handleSearch}
                                    handleSelectedEmployeesToChat={handleSelectedUsersToChat}
                                    handleSetGroupChatName={handleSetGroupChatName}
                                    hasLeadershipRoles={hasLeadershipRoles}
                                    formData={formData}
                                    setFormData={setFormData}
                                />
                                :
                                <Single
                                    searchFilteredUserList={searchFilteredUsersByType[selectedUserListType] || []}
                                    selectedUserListType={selectedUserListType}
                                    handleSearch={handleSearch}
                                    handleSelectedEmployeesToChat={(data) => handleSelectedUsersToChat(data, selectedUserListType)}
                                    hasLeadershipRoles={hasLeadershipRoles}
                                    isUserListBeingSearched={isUserListBeingSearched}
                                />
                            }
                        </SelectUsersSection>
                    </>
                }
            </FlexContainer>
        </StandardModal>
    );
};

export default CreateChatModal;
